import { HttpErrorResponse } from '@angular/common/http';
import {Injectable} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MessageService } from './message.service';
import { MessagesUtil } from '@core/utils';

export interface ProblemDetail {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class CrudErrorService {
  constructor(private readonly messageService: MessageService) {}

  handleErrorAPIWithNames(error: HttpErrorResponse, names: string[], titleDefault: string) {
    if (Array.isArray(error.error)) {
      return this.messageService.showError(error.error[0], titleDefault);
    } else if (typeof error.error === 'object') {
      names.forEach((name) => {
        if (error.error[name] !== undefined) {
          return this.messageService.showError(error.error[name][0], titleDefault);
        }
      });
    } else {
      return this.messageService.showError(MessagesUtil.get('erro.generico'), titleDefault);
    }
  }

  handleValidationErrors(error: HttpErrorResponse, form: FormGroup, titleDefault?: string): void {
    if (error.error && typeof error.error === 'object' && 'validation' in error.error) {
      const validation = error.error['validation'] as Record<string, string[]>;

      Object.entries(validation).forEach(([campo, mensagens]) => {
        const control = form.get(campo);
        if (control) {
          setTimeout(() => {
            control.setErrors({ ...control.errors, serverError: mensagens[0] });
          }, 0);
        } else {
          this.messageService.showAlert(mensagens[0], titleDefault);
        }
      });

      return;
    }

    this.handleErrorAPI(error, titleDefault);
  }

  handleErrorAPI(error: HttpErrorResponse, titleDefault?: string): void {
    const title = titleDefault ?? MessagesUtil.get('erro.atencao');

    if (error.status === 502 || error.status === 0) {
      return;
    }

    if (error.status === 401) {
      const msg = error.error?.detail || MessagesUtil.get('http.status.401.message');
      this.messageService.showError(msg, MessagesUtil.get('erro.sessao.expirada.titulo'));
      return;
    }

    if (error.status === 413) {
      const msg = error.error?.detail || MessagesUtil.get('erro.arquivo.grande');
      this.messageService.showError(msg, MessagesUtil.get('erro.arquivo.grande.titulo'));
      return;
    }

    if (error.error && typeof error.error === 'object' && 'detail' in error.error) {
      this.messageService.showError((error.error as ProblemDetail).detail!, title);
      return;
    }

    if (Array.isArray(error.error)) {
      this.messageService.showError(error.error[0], title);
      return;
    }

    if (typeof error.error === 'string') {
      const msg = error.error.length > 200 ? error.error.substring(0, 199) + '...' : error.error;
      this.messageService.showError(msg, title);
      return;
    }

    if (error.error && typeof error.error === 'object' && 'validation' in error.error) {
      const validation = error.error['validation'] as Record<string, string[]>;
      const messages = Object.values(validation).flat();
      messages.forEach(msg => this.messageService.showAlert(msg, title));
      return;
    }

    if (error.error && typeof error.error === 'object') {
      const keys = Object.keys(error.error);

      if (keys.length <= 1) {
        const message: any = Object.values(error.error)[0];
        const displayMsg = Array.isArray(message) ? message.join('\n') : String(message);
        this.messageService.showError(displayMsg, title);
      } else {
        keys.forEach(key => {
          const message: any = (error.error as any)[key];
          const displayMsg = Array.isArray(message) ? message[0] : String(message);
          this.messageService.showError(displayMsg);
        });
        this.messageService.showError('', title);
      }
      return;
    }

    this.messageService.showError(MessagesUtil.get('erro.generico'), title);
  }
}
